import { useRef, useState } from "react";
import { uploadAffiliateDocument } from "@/lib/affiliateUpload";
import type { DocumentoForm } from "./types";

interface DocumentSlotsProps {
  tipos: string[];
  docs: DocumentoForm[];
  onChange: (docs: DocumentoForm[]) => void;
  folder: string;
  hint?: string;
}

/**
 * Ranura de subida para cada documento. Los archivos van a un bucket PRIVADO:
 * nunca son visibles en el sitio público, solo el administrador puede abrirlos.
 */
export default function DocumentSlots({ tipos, docs, onChange, folder, hint }: DocumentSlotsProps) {
  const [busyTipo, setBusyTipo] = useState<string | null>(null);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const activeTipo = useRef<string>("");

  const handlePick = (tipo: string) => {
    activeTipo.current = tipo;
    inputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    const tipo = activeTipo.current;
    setError("");
    setBusyTipo(tipo);
    try {
      const { path, name } = await uploadAffiliateDocument(file, folder);
      const others = docs.filter((doc) => doc.tipo !== tipo);
      onChange([...others, { tipo, archivoUrl: path, archivoNombre: name }]);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "No pudimos subir el documento.");
    } finally {
      setBusyTipo(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (tipo: string) => onChange(docs.filter((doc) => doc.tipo !== tipo));

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {tipos.map((tipo) => {
          const doc = docs.find((item) => item.tipo === tipo);
          return (
            <div
              key={tipo}
              className={`flex items-center justify-between gap-3 rounded-md border px-3.5 py-3 ${
                doc ? "border-accent-300 bg-accent-50" : "border-background-300 bg-background-50"
              }`}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                    doc ? "bg-accent-100 text-accent-700" : "bg-secondary-100 text-secondary-900"
                  }`}
                >
                  <i
                    className={`${doc ? "ri-file-check-line" : "ri-file-add-line"} text-base leading-none`}
                    aria-hidden="true"
                  ></i>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-label font-medium text-foreground-900">{tipo}</p>
                  <p className="truncate text-xs text-foreground-500">
                    {doc ? doc.archivoNombre : "Sin archivo"}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => handlePick(tipo)}
                  disabled={busyTipo === tipo}
                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-50"
                >
                  <i
                    className={`${busyTipo === tipo ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} text-sm leading-none`}
                    aria-hidden="true"
                  ></i>
                  {doc ? "Cambiar" : "Subir"}
                </button>
                {doc && (
                  <button
                    type="button"
                    onClick={() => remove(tipo)}
                    aria-label={`Quitar ${tipo}`}
                    className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-delete-bin-6-line text-sm leading-none" aria-hidden="true"></i>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {hint && <p className="mt-2 text-xs text-foreground-500">{hint}</p>}
      {error && (
        <p className="mt-2 flex items-center gap-1 text-xs text-primary-700">
          <i className="ri-error-warning-line text-sm leading-none" aria-hidden="true"></i>
          {error}
        </p>
      )}
    </div>
  );
}